import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Enums from 'Models/Enums';

export interface IFeeOptionEntityAttributes extends IModelAttributes {
	key: string;
	name: string;
	description: string;
	feeAmount: number;
	fixedAmountFee: boolean;
	automaticallyApply: boolean;
	checkInFee: boolean;
	bookingWizardFee: boolean;
	newBookingFee: boolean;
	alterBookingFee: boolean;
	removable: boolean;
	applyForStaff: boolean;
	applyForRegularCustomer: boolean;
	applyForFixedPriceCustomers: boolean;
	disabled: boolean;
	freightBooking: boolean;
	vehicleBooking: boolean;
	passengerBooking: boolean;
	refundable: boolean;
	applicationLevel: Enums.feeApplicationLevel;

	routes: Array<Models.RouteFeeOption | Models.IRouteFeeOptionAttributes>;
	fees: Array<Models.FeeEntity | Models.IFeeEntityAttributes>;
}
@entity('FeeOptionEntity', 'Fee Option')
export default class FeeOptionEntity extends Model implements IFeeOptionEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The key for the fee.
	 */
	@observable
	@attribute<FeeOptionEntity, string>()
	public key: string;

	/**
	 * The name of the fee that will display on the front-end of the application.
	 */
	@observable
	@attribute<FeeOptionEntity, string>()
	public name: string;

	/**
	 * A description of when this fee should be applied.
	 */
	@observable
	@attribute<FeeOptionEntity, string>()
	public description: string;

	/**
	 * The amount that the fee will add on to the booking. Note: Depending on whether it is percentage or a whole dollar value.
	 */
	@observable
	@attribute<FeeOptionEntity, number>()
	public feeAmount: number;

	/**
	 * Whether or not this is a fixed dollar value fee. If it is, then the  fee amount will be an exact dollar value added to the booking. Otherwise it will be a percentage of the total booking cost.
	 */
	@observable
	@attribute<FeeOptionEntity, boolean>()
	public fixedAmountFee: boolean;

	/**
	 * Whether or not the fee will automatically apply in the situations it applies to.
	 */
	@observable
	@attribute<FeeOptionEntity, boolean>()
	public automaticallyApply: boolean;

	/**
	 * Whether or not to show this fee during check-in.
	 */
	@observable
	@attribute<FeeOptionEntity, boolean>()
	public checkInFee: boolean;

	/**
	 * Whether or not to show this fee during the booking wizard.
	 */
	@observable
	@attribute<FeeOptionEntity, boolean>()
	public bookingWizardFee: boolean;

	/**
	 * Whether or not this fee shows for new bookings or not.
	 */
	@observable
	@attribute<FeeOptionEntity, boolean>()
	public newBookingFee: boolean;

	/**
	 * Whether or not this fee appears during an alteration of a booking.
	 */
	@observable
	@attribute<FeeOptionEntity, boolean>()
	public alterBookingFee: boolean;

	/**
	 * Whether or not the fee in removable (if it is automatically applied) by staff.
	 */
	@observable
	@attribute<FeeOptionEntity, boolean>()
	public removable: boolean;

	/**
	 * Whether or not to automatically apply the fee for staff making bookings on behalf of a customer.
	 */
	@observable
	@attribute<FeeOptionEntity, boolean>()
	public applyForStaff: boolean;

	/**
	 * Whether or not to automatically apply for regular customers when they are in the same situation that applies to this fee.
	 */
	@observable
	@attribute<FeeOptionEntity, boolean>()
	public applyForRegularCustomer: boolean;

	/**
	 * Whether or not to automatically apply for fixed price customers when in the situation that aligns with this fee option.
	 */
	@observable
	@attribute<FeeOptionEntity, boolean>()
	public applyForFixedPriceCustomers: boolean;

	/**
	 * Whether or not this fee is disabled and hidden from the UI as an option.
	 */
	@observable
	@attribute<FeeOptionEntity, boolean>()
	public disabled: boolean;

	@observable
	@attribute<FeeOptionEntity, boolean>()
	public freightBooking: boolean;

	@observable
	@attribute<FeeOptionEntity, boolean>()
	public vehicleBooking: boolean;

	@observable
	@attribute<FeeOptionEntity, boolean>()
	public passengerBooking: boolean;

	@observable
	@attribute<FeeOptionEntity, boolean>()
	public refundable: boolean;

	@observable
	@attribute<FeeOptionEntity, Enums.feeApplicationLevel>()
	public applicationLevel: Enums.feeApplicationLevel = 'Transaction';

	@observable
	@attribute({ isReference: true, manyReference: true })
	public routes: Models.RouteFeeOption[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public fees: Models.FeeEntity[] = [];

	constructor(attributes?: Partial<IFeeOptionEntityAttributes>) {
		super(attributes);

		this.routes = [];
		this.fees = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IFeeOptionEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.key !== undefined) {
				this.key = attributes.key;
			}
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.feeAmount !== undefined) {
				this.feeAmount = attributes.feeAmount;
			}
			if (attributes.fixedAmountFee !== undefined) {
				this.fixedAmountFee = attributes.fixedAmountFee;
			}
			if (attributes.automaticallyApply !== undefined) {
				this.automaticallyApply = attributes.automaticallyApply;
			}
			if (attributes.checkInFee !== undefined) {
				this.checkInFee = attributes.checkInFee;
			}
			if (attributes.bookingWizardFee !== undefined) {
				this.bookingWizardFee = attributes.bookingWizardFee;
			}
			if (attributes.newBookingFee !== undefined) {
				this.newBookingFee = attributes.newBookingFee;
			}
			if (attributes.alterBookingFee !== undefined) {
				this.alterBookingFee = attributes.alterBookingFee;
			}
			if (attributes.removable !== undefined) {
				this.removable = attributes.removable;
			}
			if (attributes.applyForStaff !== undefined) {
				this.applyForStaff = attributes.applyForStaff;
			}
			if (attributes.applyForRegularCustomer !== undefined) {
				this.applyForRegularCustomer = attributes.applyForRegularCustomer;
			}
			if (attributes.applyForFixedPriceCustomers !== undefined) {
				this.applyForFixedPriceCustomers = attributes.applyForFixedPriceCustomers;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.freightBooking !== undefined) {
				this.freightBooking = attributes.freightBooking;
			}
			if (attributes.vehicleBooking !== undefined) {
				this.vehicleBooking = attributes.vehicleBooking;
			}
			if (attributes.passengerBooking !== undefined) {
				this.passengerBooking = attributes.passengerBooking;
			}
			if (attributes.refundable !== undefined) {
				this.refundable = attributes.refundable;
			}
			if (attributes.applicationLevel !== undefined) {
				this.applicationLevel = attributes.applicationLevel;
			}
			if (attributes.routes !== undefined && Array.isArray(attributes.routes)) {
				if (!this.routes) {
					this.routes = [];
				}
				for (const model of attributes.routes) {
					if (model instanceof Models.RouteFeeOption) {
						this.routes.push(model);
					} else {
						this.routes.push(new Models.RouteFeeOption(model));
					}
				}
			}
			if (attributes.fees !== undefined && Array.isArray(attributes.fees)) {
				if (!this.fees) {
					this.fees = [];
				}
				for (const model of attributes.fees) {
					if (model instanceof Models.FeeEntity) {
						this.fees.push(model);
					} else {
						this.fees.push(new Models.FeeEntity(model));
					}
				}
			}
		}
	}
}
